import type { Curve, ParsedData } from '../../../../interfaces/toolData';
import type { WaveFileType } from '../../types';
import { cleanTimeSeriesData } from '../../../dataQuality';

/**
 * Generic base parser for JAE Smart DM measurement files (GAMMA, TEMP, VOLTAGES, etc.)
 * 
 * All measurement files follow the same format:
 * - Lines 1-12: Standard metadata header
 * - Line 13: CSV column headers
 * - Line 14+: Time series data
 */
export class SmartDmMeasurementParser {
  
  /**
   * Parse a JAE Smart DM measurement file
   */
  static parse(
    fileContent: string, 
    expectedLogType: string,
    fileType: WaveFileType,
    unitMappings: Record<string, string> = {}
  ): ParsedData {
    const lines = fileContent.split('\n');
    
    // Validate file format
    if (!lines[0]?.trim().startsWith('SmartDM')) {
      throw new Error('Invalid JAE Smart DM file format');
    }
    
    if (!lines[1]?.trim().includes(`${expectedLogType} Log`)) {
      throw new Error(`Not a JAE Smart DM ${expectedLogType} file`);
    }
    
    // Extract metadata from header (everything before the CSV data)
    const { metadata, dataHeaderIndex, dataStartIndex } = this.findDataStart(lines);
    
    if (dataHeaderIndex === -1) {
      throw new Error(`Could not find data header in JAE Smart DM ${expectedLogType} file`);
    }
    
    // Parse CSV data manually
    const headerLine = lines[dataHeaderIndex];
    const columnNames = headerLine.split(',').map(name => name.trim());
    
    const dataLines = lines.slice(dataStartIndex).filter(line => line.trim());
    const rawData: Record<string, string>[] = [];
    
    for (const line of dataLines) {
      const values = line.split(',');
      const record: Record<string, string> = {};
      
      columnNames.forEach((colName, index) => {
        if (index < values.length) {
          record[colName] = values[index]?.trim() || '';
        }
      });
      
      rawData.push(record);
    }
    
    if (rawData.length === 0) {
      throw new Error(`No valid data found in JAE Smart DM ${expectedLogType} file`);
    }
    
    // Generate curves from time series data
    const curves = this.generateCurves(rawData, unitMappings);
    
    return {
      toolType: 'JAE',
      fileType,
      curves,
      metadata,
      dataPreview: {
        totalCount: rawData.length,
        first5: rawData.slice(0, 5)
      }
    };
  }
  
  private static findDataStart(lines: string[]): {
    metadata: Record<string, string>;
    dataHeaderIndex: number;
    dataStartIndex: number;
  } {
    const metadata: Record<string, string> = {};
    let dataHeaderIndex = -1;
    
    // Search for the CSV header line by looking for timestamp column names
    const timestampColumns = ['RTC_TIME', 'Time', 'TIME', 'Timestamp'];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Check if this line contains a timestamp column (likely the CSV header)
      const hasTimestampColumn = timestampColumns.some(col => 
        line.includes(col) && line.includes(',')
      );
      
      if (hasTimestampColumn) {
        dataHeaderIndex = i;
        break;
      }
      
      // Extract metadata from lines before the CSV header
      if (line.includes(':') && !line.includes(',')) {
        const [key, ...valueParts] = line.split(':');
        const value = valueParts.join(':').trim();
        
        if (key && value) {
          metadata[key.trim()] = value;
        }
      }
    }
    
    return {
      metadata,
      dataHeaderIndex,
      dataStartIndex: dataHeaderIndex + 1
    };
  }
  
  private static generateCurves(
    data: Record<string, string>[], 
    unitMappings: Record<string, string>
  ): Curve[] {
    const curves: Curve[] = [];
    
    if (data.length === 0) return curves;
    
    // Find the timestamp column
    const timestampCol = this.findTimestampColumn(data[0]);
    if (!timestampCol) {
      throw new Error('No timestamp column found in measurement data');
    }
    
    // Get all non-timestamp columns for measurements
    const measurementColumns = Object.keys(data[0]).filter(col => col !== timestampCol);
    
    for (const colName of measurementColumns) {
      const timeValues: Date[] = [];
      const measurementValues: number[] = [];
      
      for (const row of data) {
        const timestampStr = row[timestampCol];
        const valueStr = row[colName];
        
        if (!timestampStr || !valueStr) continue;
        
        // Parse timestamp (handle both "RTC_TIME" and "Time" formats)
        const timestamp = this.parseTimestamp(timestampStr);
        if (isNaN(timestamp.getTime())) continue;
        
        // Parse measurement value
        const value = parseFloat(valueStr);
        if (isNaN(value)) continue;
        
        timeValues.push(timestamp);
        measurementValues.push(value);
      }
      
      if (timeValues.length > 0) {
        // Clean the time series data
        const cleanupResult = cleanTimeSeriesData({
          name: colName,
          xValues: timeValues,
          yValues: measurementValues,
          yUnit: unitMappings[colName] || 'unknown'
        });
        
        curves.push(cleanupResult.cleanedCurve);
      }
    }
    
    return curves;
  }
  
  private static findTimestampColumn(sampleRow: Record<string, string>): string | null {
    const timestampColumns = ['RTC_TIME', 'Time', 'TIME', 'Timestamp'];
    
    for (const col of timestampColumns) {
      if (col in sampleRow) {
        return col;
      }
    }
    
    return null;
  }
  
  private static parseTimestamp(timestampStr: string): Date {
    // Handle different timestamp formats:
    // "07/08/2025 12:29:35" (standard RTC_TIME)
    // "07/08/2025 12:42:11 0" (PULSE format with extra data)
    
    // Remove extra data after the time if present
    const timePart = timestampStr.split(' ').slice(0, 2).join(' ');
    
    return new Date(timePart);
  }
} 